'use client'

import React, { useState, useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { Heart } from 'lucide-react'

import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useToggleScheduleFavorite } from '@/core/hooks/useSchedule'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

interface FavoriteButtonProps {
	scheduleId: string
	isFavorite?: boolean
	className?: string
}

export const FavoriteButton = ({
	scheduleId,
	isFavorite = false,
	className = '',
}: FavoriteButtonProps) => {
	const queryClient = useQueryClient()
	const { showNotification } = useNotificationContext()
	const [optimisticFavorite, setOptimisticFavorite] = useState(isFavorite)
	const [isLoading, setIsLoading] = useState(false)

	const { mutateAsync: toggleFavorite } = useToggleScheduleFavorite({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['schedules-assigned'] })
				queryClient.invalidateQueries({ queryKey: ['schedules'] })
				queryClient.invalidateQueries({ queryKey: ['schedules-favorites'] })
			},
		},
	})

	const handleFavoriteClick = useCallback(
		async (e: React.MouseEvent) => {
			e.stopPropagation()

			if (isLoading) return

			const previousFavorite = optimisticFavorite
			const newFavorite = !previousFavorite
			setOptimisticFavorite(newFavorite)
			setIsLoading(true)

			try {
				await toggleFavorite({
					scheduleId,
					favorite: newFavorite,
				})
			} catch {
				setOptimisticFavorite(previousFavorite)
				showNotification({
					type: 'error',
					message: 'Failed to update favorite',
					description: 'Please try again.',
				})
			} finally {
				setIsLoading(false)
			}
		},
		[
			scheduleId,
			optimisticFavorite,
			isLoading,
			toggleFavorite,
			showNotification,
		]
	)

	return (
		<button
			className={`md:h-8 md:w-8 h-6 w-6 rounded-full border border-muted-foreground/20 dark:border-action-item-border bg-outline backdrop-blur-sm flex items-center justify-center hover:bg-black/60 transition-colors ${className}`}
			onClick={handleFavoriteClick}
			disabled={isLoading}
		>
			<Heart
				className={`md:h-4 md:w-4 h-3 w-3 transition-colors ${optimisticFavorite ? 'fill-white dark:text-white text-foreground' : 'dark:text-white text-foreground'}`}
			/>
		</button>
	)
}
